/**
 * InactivityTimerService.js
 * Production-grade inactivity monitoring with cross-tab synchronization.
 * Monitors user interaction and triggers warning / auto-logout cleanly.
 */

// Default Configuration (30 minutes total, 2 minutes warning countdown)
export const DEFAULT_INACTIVITY_CONFIG = {
  timeoutMs: 30 * 60 * 1000,    // 30 minutes
  warningMs: 2 * 60 * 1000,     // 2 minutes
  throttleActivityMs: 10 * 1000 // Throttle activity updates to once per 10s
};

class InactivityTimerService {
  constructor() {
    this.timeoutMs = DEFAULT_INACTIVITY_CONFIG.timeoutMs;
    this.warningMs = DEFAULT_INACTIVITY_CONFIG.warningMs;
    this.throttleActivityMs = DEFAULT_INACTIVITY_CONFIG.throttleActivityMs;
    
    this.warningTimer = null;
    this.logoutTimer = null;
    this.countdownInterval = null;
    
    this.onWarningCallback = null;
    this.onTimeoutCallback = null;
    this.onActivityCallback = null;
    
    this.lastActivityTime = Date.now();
    this.lastBroadcastTime = 0;
    this.isRunning = false;
    this.isWarningShown = false;

    this.boundHandleUserActivity = this.handleUserActivity.bind(this);
    this.boundHandleStorageEvent = this.handleStorageEvent.bind(this);
  }

  /**
   * Start tracking user activity
   */
  start({ timeoutMs, warningMs, onWarning, onTimeout, onActivity } = {}) {
    if (this.isRunning) {
      this.stop();
    }

    if (timeoutMs) this.timeoutMs = timeoutMs;
    if (warningMs) this.warningMs = warningMs;
    this.onWarningCallback = onWarning || null;
    this.onTimeoutCallback = onTimeout || null;
    this.onActivityCallback = onActivity || null;

    this.isRunning = true;
    this.isWarningShown = false;
    this.lastActivityTime = Date.now();

    // Attach passive listeners to user interaction events
    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach(event => {
      window.addEventListener(event, this.boundHandleUserActivity, { passive: true });
    });

    // Cross-tab synchronization via localStorage events
    window.addEventListener('storage', this.boundHandleStorageEvent);

    this.scheduleTimers();
  }

  /**
   * Throttled activity event handler
   */
  handleUserActivity() {
    if (!this.isRunning) return;

    const now = Date.now();
    this.lastActivityTime = now;

    // Broadcast activity to other tabs at most once per throttleActivityMs
    if (now - this.lastBroadcastTime > this.throttleActivityMs) {
      this.lastBroadcastTime = now;
      try {
        localStorage.setItem('scms_last_activity_sync', String(now));
      } catch (e) {}
    }

    // If warning was active and user interacted, dismiss warning and reset
    if (this.isWarningShown) {
      this.reset();
      if (this.onActivityCallback) {
        this.onActivityCallback();
      }
    } else {
      this.scheduleTimers();
    }
  }

  /**
   * Handle activity / logout events triggered from other browser tabs
   */
  handleStorageEvent(e) {
    if (!this.isRunning) return;

    if (e.key === 'scms_last_activity_sync' && e.newValue) {
      const remoteTime = parseInt(e.newValue, 10);
      if (!isNaN(remoteTime) && remoteTime > this.lastActivityTime) {
        this.lastActivityTime = remoteTime;
        if (this.isWarningShown) {
          this.reset();
          if (this.onActivityCallback) {
            this.onActivityCallback();
          }
        } else {
          this.scheduleTimers();
        }
      }
    } else if (e.key === 'scms_cross_tab_logout') {
      // Another tab performed logout -> terminate immediately
      this.stop();
      if (this.onTimeoutCallback) {
        this.onTimeoutCallback({ isCrossTab: true });
      }
    }
  }

  /**
   * Schedule countdown timers
   */
  scheduleTimers() {
    this.clearTimers();

    const warningDelay = Math.max(0, this.timeoutMs - this.warningMs);
    const logoutDelay = this.timeoutMs;

    // Timer to trigger warning
    this.warningTimer = setTimeout(() => {
      this.triggerWarning();
    }, warningDelay);

    // Hard fallback timer for automatic logout
    this.logoutTimer = setTimeout(() => {
      this.triggerTimeout();
    }, logoutDelay);
  }

  /**
   * Trigger inactivity warning
   */
  triggerWarning() {
    if (!this.isRunning || this.isWarningShown) return;

    this.isWarningShown = true;
    let remainingSeconds = Math.round(this.warningMs / 1000);

    if (this.onWarningCallback) {
      this.onWarningCallback(remainingSeconds);
    }

    // Interval to update countdown every second
    this.countdownInterval = setInterval(() => {
      remainingSeconds -= 1;
      if (remainingSeconds <= 0) {
        clearInterval(this.countdownInterval);
        this.triggerTimeout();
      } else if (this.onWarningCallback) {
        this.onWarningCallback(remainingSeconds);
      }
    }, 1000);
  }

  /**
   * Trigger automatic logout
   */
  triggerTimeout() {
    if (!this.isRunning) return;

    this.stop();
    // Broadcast logout to all other open tabs
    try {
      localStorage.setItem('scms_cross_tab_logout', String(Date.now()));
    } catch (e) {}

    if (this.onTimeoutCallback) {
      this.onTimeoutCallback({ isCrossTab: false });
    }
  }

  /**
   * Reset the timer (e.g. when user clicks "Continue Session")
   */
  reset() {
    if (!this.isRunning) return;

    this.isWarningShown = false;
    this.lastActivityTime = Date.now();
    this.lastBroadcastTime = Date.now();
    try {
      localStorage.setItem('scms_last_activity_sync', String(Date.now()));
    } catch (e) {}

    this.scheduleTimers();
  }

  /**
   * Clear all pending timer callbacks
   */
  clearTimers() {
    if (this.warningTimer) clearTimeout(this.warningTimer);
    if (this.logoutTimer) clearTimeout(this.logoutTimer);
    if (this.countdownInterval) clearInterval(this.countdownInterval);
    this.warningTimer = null;
    this.logoutTimer = null;
    this.countdownInterval = null;
  }

  /**
   * Stop tracking completely (e.g. upon user logout)
   */
  stop() {
    this.isRunning = false;
    this.isWarningShown = false;
    this.clearTimers();

    const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
    events.forEach(event => {
      window.removeEventListener(event, this.boundHandleUserActivity);
    });
    window.removeEventListener('storage', this.boundHandleStorageEvent);
  }
}

const inactivityTimerService = new InactivityTimerService();
export default inactivityTimerService;
